import EditTradeContext from "@shared/features/trades/EditTrade/EditTrade.context";
import { LanguageNameSpaces, OpenMessage, OrderStatus } from "@shared/types";
import { ErrorTypes, OrderTypes } from "@shared/features/trades/common/types";
import { validateOrderRelativePrice } from "@shared/features/tradingTerminal/common/utils";
import {
  getErrorDisplayMessage,
  getFilledTradeAmount,
  orderHasStatus,
} from "@shared/features/trades/common/utils";
import { EditOrderData } from "@shared/features/trades/EditTrade/types";
import { useContext, useState } from "react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface useEditOrderActionsProps {
  order: EditOrderData;
  orderType: OrderTypes;
  orderIndex: number;
  openMessage: OpenMessage;
}

const useEditOrderActions = ({
  order,
  orderType,
  orderIndex,
  openMessage,
}: useEditOrderActionsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const {
    getOrdersByType,
    cancelOrder,
    moveOrderToCurrentPrice,
    moveStopOrderToBreakevenPrice,
    entryOrders,
    takeProfitOrders,
    breakevenPrice,
    confirmWithCallback,
    updateOrder,
    currentPrice,
    ordersData,
  } = useContext(EditTradeContext)!;
  const [editOrderModalOpen, setEditOrderModalOpen] = useState(false);

  const orders = getOrdersByType(orderType);
  const otherPendingOrders = orders.filter(
    (order, index) =>
      index !== orderIndex && order.status === OrderStatus.PENDING
  );
  const otherPendingNonFulfilledOrders = otherPendingOrders.filter(
    (order) => !order.filled_quantity
  );
  const invalidCancelAndRedistribute =
    otherPendingNonFulfilledOrders.length === 0;
  const invalidEditOrder = order.filled_quantity > 0;

  const moveToCurrentPriceErrors =
    orderType !== OrderTypes.STOP
      ? validateOrderRelativePrice(
          currentPrice,
          orderType,
          !!ordersData?.data.reverse,
          !!ordersData?.data.breakout,
          currentPrice,
          entryOrders.filter((order) => order.status === OrderStatus.PENDING),
          takeProfitOrders.filter(
            (order) => order.status === OrderStatus.PENDING
          )
        )
      : [];

  const invalidBreakevenPriceError =
    orderType === OrderTypes.STOP &&
    !getFilledTradeAmount(getOrdersByType(OrderTypes.ENTRY));

  const moveToBreakevenPriceErrors =
    orderType === OrderTypes.STOP
      ? validateOrderRelativePrice(
          breakevenPrice,
          orderType,
          !!ordersData?.data.reverse,
          !!ordersData?.data.breakout,
          currentPrice,
          [],
          [],
          false
        )
      : [];

  const onUpdateOrder = (updatedOrder: EditOrderData) =>
    updateOrder(orderType, orderIndex, updatedOrder);

  const onMoveToCurrentPriceClick = () => {
    if (moveToCurrentPriceErrors.length > 0) {
      openMessage(
        getErrorDisplayMessage(t, moveToCurrentPriceErrors[0], orderType),
        "warning"
      );
    } else {
      moveOrderToCurrentPrice(orderType, orderIndex);
    }
  };

  const onMoveToBreakEvenPriceClick = async () => {
    if (moveToBreakevenPriceErrors.length > 0) {
      openMessage(
        getErrorDisplayMessage(t, moveToBreakevenPriceErrors[0], orderType),
        "warning"
      );
      return;
    } else if (invalidBreakevenPriceError) {
      openMessage(t("warnings.no_fulfilled_entries"), "warning");
      return;
    }

    const entryOrdersBelowBreakevenPrice: number[] = entryOrders
      .map((order, index) => {
        const isBreakevenPriceCancelsTarget = ordersData?.data.reverse
          ? order.edit_target_price >= breakevenPrice
          : order.edit_target_price <= breakevenPrice;
        if (
          orderHasStatus(order, false, OrderStatus.PENDING) &&
          isBreakevenPriceCancelsTarget
        ) {
          return index;
        }
        return -1;
      })
      .filter((index) => index > -1);
    const numEntryOrdersBelowBreakevenPrice =
      entryOrdersBelowBreakevenPrice.length;
    if (numEntryOrdersBelowBreakevenPrice > 0) {
      const okPressed = await confirmWithCallback(
        `Moving the stop-loss price to break-even price (${breakevenPrice}) will cancel ${
          entryOrdersBelowBreakevenPrice.length
        } pending entr${
          entryOrdersBelowBreakevenPrice.length > 1 ? "ies" : "y"
        } ${
          !ordersData?.data.reverse ? "below" : "above"
        } this price. Are you sure you wish to continue?`
      );
      if (okPressed)
        moveStopOrderToBreakevenPrice(entryOrdersBelowBreakevenPrice);
    } else {
      moveStopOrderToBreakevenPrice(entryOrdersBelowBreakevenPrice);
    }
  };
  const onCancelAndRedistributeClick = () => {
    if (invalidCancelAndRedistribute) {
      const error = !otherPendingOrders.length
        ? ErrorTypes.CANT_REDISTRIBUTE_NO_SIBLINGS_TARGETS
        : ErrorTypes.CANT_REDISTRIBUTE_NO_SIBLINGS_NON_FULFILLED_TARGETS;
      openMessage(getErrorDisplayMessage(t, error), "warning");
    } else {
      cancelOrder(orderType, orderIndex, true);
    }
  };

  const onEditOrderClick = () => {
    if (invalidEditOrder) {
      openMessage(t("warnings.partially_fulfilled_edit"), "warning");
    } else {
      setEditOrderModalOpen(true);
    }
  };

  const onCancelClick = () => {
    cancelOrder(orderType, orderIndex);
  };

  return {
    editOrderModalOpen,
    setEditOrderModalOpen,
    onEditOrderClick,
    onUpdateOrder,
    onCancelClick,
    onCancelAndRedistributeClick,
    onMoveToCurrentPriceClick,
    onMoveToBreakEvenPriceClick,
    invalidEditOrder,
    invalidCancelAndRedistribute,
    moveToCurrentPriceErrors,
    moveToBreakevenPriceErrors,
    invalidBreakevenPriceError,
  };
};

export default useEditOrderActions;
